"use client";

import {
  Suspense,
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";

/** 与会话页签绑定；不写进 URL，避免与 debug 等 query 混拼 */
const DEMO_SESSION_KEY = "findin:demo-session";

const DemoModeContext = createContext(false);

function readDemoStored(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return sessionStorage.getItem(DEMO_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function DemoModeProviderInner({ children }: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  const demoParam = searchParams.get("demo");
  const [sessionOn, setSessionOn] = useState(false);

  useLayoutEffect(() => {
    if (demoParam === "true") {
      try {
        sessionStorage.setItem(DEMO_SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      setSessionOn(true);
      return;
    }
    if (demoParam === "false") {
      try {
        sessionStorage.removeItem(DEMO_SESSION_KEY);
      } catch {
        /* ignore */
      }
      setSessionOn(false);
      return;
    }
    setSessionOn(readDemoStored());
  }, [demoParam]);

  const value = useMemo(() => {
    if (demoParam === "true") {
      return true;
    }
    if (demoParam === "false") {
      return false;
    }
    return sessionOn;
  }, [demoParam, sessionOn]);

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
}

/**
 * - 访问任意已接入 DemoModeProvider 的 URL 且带 **`demo=true`**：开启 demo（并写入 sessionStorage，后续跳转可不带 demo）。
 * - 访问任意已接入 DemoModeProvider 的 URL 且带 **`demo=false`**：关闭 demo（并清除 sessionStorage）。
 * - 中间路由无需拼 `demo`，可与 **`debug=true`** 等其它 query 同时使用，互不影响。
 */
export function DemoModeProvider({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<DemoModeContext.Provider value={false}>{children}</DemoModeContext.Provider>}>
      <DemoModeProviderInner>{children}</DemoModeProviderInner>
    </Suspense>
  );
}

export function useDemoMode() {
  return useContext(DemoModeContext);
}
